import { createPortal } from 'react-dom';
import styles from './Tour.module.css';

interface SpotlightBackdropProps {
  rect: DOMRect;
  /** Optional padding around the anchor rect, in px. */
  padding?: number;
}

/**
 * Renders 4 dim rectangles around `rect` (top / left / right / bottom) so the
 * area inside `rect` stays unblurred — i.e. spotlights it. Plus a thin ring
 * around the rect for emphasis. Portaled to document.body so it overlays
 * everything regardless of stacking context.
 *
 * We avoid `clip-path` because Safari's `backdrop-filter` doesn't honor clip
 * paths consistently (Webkit bug). Four positioned divs are robust and
 * cheaper to recompute.
 */
export function SpotlightBackdrop({ rect, padding = 4 }: SpotlightBackdropProps) {
  const top = rect.top - padding;
  const left = rect.left - padding;
  const right = rect.right + padding;
  const bottom = rect.bottom + padding;
  const width = rect.width + padding * 2;
  const height = rect.height + padding * 2;

  return createPortal(
    <>
      {/* Top */}
      <div
        className={styles.backdropPart}
        aria-hidden="true"
        style={{ top: 0, left: 0, right: 0, height: Math.max(top, 0) }}
      />
      {/* Bottom */}
      <div
        className={styles.backdropPart}
        aria-hidden="true"
        style={{ top: bottom, left: 0, right: 0, bottom: 0 }}
      />
      {/* Left */}
      <div
        className={styles.backdropPart}
        aria-hidden="true"
        style={{ top: Math.max(top, 0), left: 0, width: Math.max(left, 0), height }}
      />
      {/* Right */}
      <div
        className={styles.backdropPart}
        aria-hidden="true"
        style={{ top: Math.max(top, 0), left: right, right: 0, height }}
      />
      {/* Spotlight ring */}
      <div
        className={styles.ring}
        aria-hidden="true"
        style={{ top, left, width, height }}
      />
    </>,
    document.body
  );
}
